import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { Button } from "@progress/kendo-react-buttons";
import { SvgIcon } from "@progress/kendo-react-common";
import { DatePicker, TimePicker } from "@progress/kendo-react-dateinputs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import { NumericTextBox, RadioButton, RadioButtonChangeEvent, Slider, Switch } from "@progress/kendo-react-inputs";
import { useLocalization } from "@progress/kendo-react-intl";
import { Avatar } from "@progress/kendo-react-layout";
import { ListView } from "@progress/kendo-react-listview";
import { arrowRotateCwIcon, chartScatterSmoothLinesMarkersIcon, optimizationIcon, trashIcon } from "@progress/kendo-svg-icons";
import axios from "axios";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import { AccessToken } from "../../../config/AccessToken";
import { getLikelihoodColor } from "../../../config/likelihoodColors";
import { loadingPanel } from "../../LoadingPanel";
import { NotificationGroup, Notification } from "@progress/kendo-react-notification";
import { Fade } from "@progress/kendo-react-animation";
import { currentLocation } from "../../../assets/current-location";
import { AzureMapComponent } from "../../../pages/AzureMapComponent";
import React from "react";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { Card, CardHeader, CardBody } from "@progress/kendo-react-layout";
import { Chart, ChartTitle, ChartLegend, ChartSeries, ChartTooltip, ChartSeriesItem } from "@progress/kendo-react-charts";
import { AzureMapSpaghettiChart } from "./AzureMapSpaghettiChart";


// Componente memoizzato per il grafico per evitare re-render inutili
const AccuracyChart = React.memo(({ data, localizationService }: { data: any[], localizationService: any }) => {
    const totalPoints = data.reduce((sum, item) => sum + item.value, 0);

    const tooltipRender = (context: any) => {
        const { category, color } = context?.dataItem ?? context?.point?.dataItem;

        return (
            <div style={{
                padding: '10px',
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                minWidth: '200px'
            }}>
                <div style={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    paddingBottom: '6px',
                    borderBottom: '1px solid #e0e0e0',
                    color: 'black'
                }}>
                    {localizationService.toLanguageString('configuration.analysis.path_history.accuracy_distribution', 'NOT_FOUND')}
                </div>
                {data.map((item, index) => {
                    const percentage = totalPoints > 0 ? ((item.value / totalPoints) * 100).toFixed(1) : 0;
                    const isCurrentCategory = item.category === category;

                    return (
                        <div key={index} style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '4px',
                            padding: '3px',
                            background: isCurrentCategory ? '#f0f0f0' : 'transparent',
                            borderRadius: '3px',
                            fontWeight: isCurrentCategory ? 'bold' : 'normal'
                        }}>
                            <div style={{
                                width: '10px',
                                height: '10px',
                                backgroundColor: item.color,
                                marginRight: '8px',
                                borderRadius: '2px',
                                flexShrink: 0
                            }}></div>
                            <div style={{
                                fontSize: '12px',
                                color: 'black',
                                flex: 1,
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: '8px'
                            }}>
                                <span>{item.category}:</span>
                                <span><strong>{item.value}</strong> ({percentage}%)</span>
                            </div>
                        </div>
                    );
                })}
                {totalPoints > 0 && (
                    <div style={{
                        marginTop: '8px',
                        paddingTop: '6px',
                        borderTop: '1px solid #e0e0e0',
                        fontSize: '12px',
                        color: 'black',
                        fontWeight: 'bold'
                    }}>
                        {localizationService.toLanguageString('configuration.analysis.path_history.total', 'NOT_FOUND')}: {totalPoints} {localizationService.toLanguageString('configuration.analysis.path_history.points', 'NOT_FOUND')}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Chart onSeriesClick={(e) => { }} style={{ width: '250px', height: '150px', margin: '0 auto' }}>
            <ChartTitle visible={false} />
            <ChartLegend position="right" offsetX={-15} />
            <ChartTooltip render={tooltipRender} />
            <ChartSeries>
                <ChartSeriesItem
                    type="pie"
                    data={data}
                    categoryField="category"
                    field="value"
                    colorField="color"
                />
            </ChartSeries>
        </Chart>
    );
});

export const SpaghettiChart = () => {

    const SERVER_URL = process.env.REACT_APP_SERVER_URL;
    const localizationService = useLocalization();
    const [instance, accessTokenRequest] = AccessToken();
    let languageId: any = localStorage.getItem('languageId');

    const [loading, setLoading] = useState(false);

    const [devicesList, setDevicesList] = useState<any>([]);
    const [devicesWithSelection, setDevicesWithSelection] = useState<any>([]);
    const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
    const [focusedItemMac, setFocusedItemMac] = useState<string | null>(null);
    const [searchValue, setSearchValue] = useState('');

    const [checkedOnlyBound, setCheckedOnlyBound] = useState(false);
    const [checked, setChecked] = useState(false);
    const [isAdvanced, setIsAdvanced] = useState(false);
    const [analyzeEnabled, setAnalyzeEnabled] = useState(false);
    const [distance, setDistance] = useState(0);

    const [dateTimeState, setDateTimeState] = useState<any>({
        fromDate: new Date(),
        toDate: new Date(),
        fromHour: new Date(),
        toHour: new Date()
    });

    const [intervalTimeValue, setIntervalTimeValue] = useState<any>(null);
    const [intervalTimeName, setIntervalTimeName] = useState<any>('');
    const [formattedIntervalDate, setFormattedIntervalDate] = useState<any>(null);
    const [spaghettiChartMessage, setSpaghettiChartMessage] = useState<any>(null);

    const [infoDevice, setInfoDevice] = useState<any>([])
    const [toDateLabel, setToDateLabel] = useState<any>('')
    const [progressiveDateLabel, setProgressiveDateLabel] = useState<any>('')

    const intervalTimeList = {
        "data": [
            {
                "id": -1,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.custom', 'NOT_FOUND'),
                "value": -1
            },
            {
                "id": 0,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_1_hour', 'NOT_FOUND'),
                "value": 60
            },
            {
                "id": 1,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_3_hours', 'NOT_FOUND'),
                "value": 180
            },
            {
                "id": 2,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_24_hours', 'NOT_FOUND'),
                "value": 1440
            },
            {
                "id": 3,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.today', 'NOT_FOUND'),
                "value": 0
            },
            {
                "id": 4,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.from_yesterday', 'NOT_FOUND'),
                "value": 1
            },
            {
                "id": 5,
                "name": localizationService.toLanguageString('configuration.analysis.path_history.last_3_days', 'NOT_FOUND'),
                "value": 4320
            },
        ]
    }

    const [error, setError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [warningMessage, setWarningMessage] = useState(false);


    const [mapGeoreferenced, setMapGeoreferenced] = useState(false);
    const [listMaps, setListMaps] = useState([]);
    const [listMapNames, setListMapNames] = useState([]);
    const [mapName, setMapName] = useState([]);
    const [selectedMap, setSelectedMap] = useState<any>([]);
    const [showZone, setShowZone] = useState<any>(false);
    const [showZoneMapGeo, setShowZoneMapGeo] = useState<any>([]);
    const [showInfoDevice, setShowInfoDevice] = useState<any>(false);
    const [showAllPoints, setShowAllPoints] = useState<any>(false);
    const [showColor, setShowColor] = useState<any>(false);
    const [signalsGrouping, setSignalsGrouping] = useState<any>(false);
    const [averagePositions, setAveragePositions] = useState<any>(false);
    const [groupingType, setGroupingType] = useState<'near' | 'each'>('near');
    const [groupingValues, setGroupingValues] = useState({ near: 0, each: 0 });
    const [isValid, setIsValid] = useState<any>(false);

    const [toggleGeoStatic, setToggleGeoStatic] = useState<any>(true);



    const mapRef: any = useRef(null);
    const mapCanvasRef: any = useRef(null)
    const zoneCanvasRef: any = useRef(null);
    const lineCanvasRef: any = useRef(null);
    const markerCanvasRef: any = useRef(null);
    const sliderFrameRef = useRef<number | null>(null);
    const markerHoverDataRef = useRef<{ point: any; label: string } | null>(null);
    const markerCanvasSizeRef = useRef<{ width: number; height: number; ratio: number }>({ width: 0, height: 0, ratio: 1 });
    const lastSliderIndexRef = useRef<number>(-1);

    const itemRefs: any = useRef({});

    type Coordinate = [number, number]; // una coppia [lat, lng]
    type CoordinateGroup = { coord: Coordinate[] };
    const [coordinates, setCoordinates] = useState<CoordinateGroup[]>([]);

    const [renderCoordinates, setRenderCoordinates] = useState<any[]>([]);

    const [step, setStep] = useState(100);
    const timeSliderMin = 0;
    const timeSliderMax = 100;

    const [countWakeups, setCountWakeups] = useState<number>(0);

    const [noData, setNoData] = useState(true);
    const [messageNoData, setMessageNoData] = useState(false);
    const [messageTooData, setMessageTooData] = useState(false);

    const [mapImageWidth, setMapImageWidth] = useState<any>(0);
    const [mapImageHeight, setMapImageHeight] = useState<any>(0);

    const COUNTER_MAX_POINTS: number = Number(process.env.REACT_APP_COUNTER_MAX_POINTS) || 32000;

    const getDeviceList = async (flag: boolean, onlyBound?: any) => {

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            await axios.get(`${SERVER_URL}/device/active`, {
                headers,
                params: {
                    enabled: flag
                }
            })
                .then((res) => {
                    const data: any = res?.data?.data
                    let devicesWithSelection = [];

                    if (flag) {
                        if (onlyBound) {
                            const boundDevices = data.filter((device: any) => device.name != null);
                            setDevicesList(boundDevices);
                            devicesWithSelection = boundDevices.map((d: any) => ({ ...d, selected: false }));
                            setDevicesWithSelection(devicesWithSelection);
                        } else {
                            setDevicesList(data);
                            devicesWithSelection = data.map((d: any) => ({ ...d, selected: false }));
                            setDevicesWithSelection(devicesWithSelection);
                        }
                    }
                    setLoading(false);
                }).catch((data) => {
                    setError(data?.message);
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    //  call API on page load
    useEffect(() => {
        getDeviceList(true)
        getMapList();
    }, [])

    const onFilterChange = (ev: any) => {
        const value = ev?.toLowerCase();
        setSearchValue(value);

        const filtered = devicesList.filter((device: any) =>
            Object.values(device).some(v =>
                v?.toString().toLowerCase().includes(value)
            )
        );

        // Mantieni lo stato di selezione
        setDevicesWithSelection(
            filtered.map((d: any) => ({
                ...d,
                selected: selectedDevices.includes(d.mac),
            }))
        );
    };

    const toggleDeviceSelection = (deviceMac: string) => {

        setMapName([])
        setFocusedItemMac(null)
        setAnalyzeEnabled(false)
        //setIntervalTimeValue(null)
        //setIntervalTimeName(null)
        setIsValid(false)

        setSelectedDevices(prev => {
            let newSelected;
            const isSelected = prev.includes(deviceMac);

            if (isSelected) {
                newSelected = prev.filter(mac => mac !== deviceMac);
            } else {
                if (prev.length >= 3) return prev;
                newSelected = [...prev, deviceMac];
            }

            setDevicesWithSelection((prevDevices: any) =>
                prevDevices.map((d: any) =>
                    d.mac === deviceMac ? { ...d, selected: !d.selected } : d
                )
            );

            if (newSelected.length === 0) {
                setFocusedItemMac(null);
            } else if (!isSelected) {
                setFocusedItemMac(deviceMac);
            } else {
                if (focusedItemMac === deviceMac) {
                    setFocusedItemMac(newSelected[0] || null);
                }
            }

            newSelected.length === 0 ?
                (
                    setAnalyzeEnabled(false),
                    setChecked(false),
                    setIsValid(false),
                    setIsAdvanced(false),
                    setIntervalTimeValue(null),
                    setIntervalTimeName(null)
                )
                :
                null

            return newSelected;
        });

    };

    const convertToSystemLocal = (date: Date): string => {
        const utc = date.getTime()

        const localDate = new Date(utc);

        const pad = (n: number) => String(n).padStart(2, '0');
        return `${pad(localDate.getDate())}/${pad(localDate.getMonth() + 1)}/${localDate.getFullYear()}, ${pad(localDate.getHours())}:${pad(localDate.getMinutes())}:${pad(localDate.getSeconds())}`;
    }

    const onChangeIntervalTime = (ev: any) => {
        if (!ev?.value) {
            return;
        }

        const now = new Date();

        const midnightToday = new Date();
        midnightToday.setHours(0, 0, 0, 0);

        //const midnightYesterday = new Date(midnightToday.getTime() - 24 * 60 * 60 * 1000);
        const midnightYesterday = new Date(midnightToday);
        midnightYesterday.setDate(midnightYesterday.getDate() - 1);

        const midnightThreeDaysAgo = new Date(midnightToday);
        midnightThreeDaysAgo.setDate(midnightThreeDaysAgo.getDate() - 3);

        let timeAgo: Date = now;

        switch (ev.value.id) {
            case -1: // custom range
                setIsAdvanced(true);
                setIntervalTimeName(ev.value);
                setChecked(true);
                return;

            case 0: // last 1 hour
            case 1: // last 3 hours
            case 2: // last 24 hours
                timeAgo = new Date(now.getTime() - ev.value.value * 60 * 1000);
                setIsAdvanced(false);
                break;

            case 3: // today
                timeAgo = midnightToday;
                setIsAdvanced(false);
                break;

            case 4: // from yesterday
                timeAgo = midnightYesterday;
                setIsAdvanced(false);
                break;

            case 5: // last 3 days
                timeAgo = midnightThreeDaysAgo;
                setIsAdvanced(false);
                break;

            default:
                return;
        }


        const formattedIntervalDate = convertToSystemLocal(timeAgo);

        const startDate = timeAgo.toISOString();

        setIntervalTimeValue(startDate);
        setIntervalTimeName(ev.value);
        setFormattedIntervalDate(formattedIntervalDate);

        setChecked(true);
    }

    const switchOnlyBound = (event: any) => {
        setCheckedOnlyBound(event.target.value);
        getDeviceList(true, event.target.value)
        setMapName([])
        setIntervalTimeName(null)
        setSelectedDevices([])
        setFocusedItemMac(null)
        setChecked(false)
    };

    const mergeDateAndTime = (date: Date, time: Date) => {
        const merged = new Date(date);
        merged.setHours(time.getHours(), time.getMinutes(), time.getSeconds(), 0);
        return merged;
    };

    const selectedRangeDates = useMemo(() => {
        const now = new Date();

        if (checked && isAdvanced) {
            const fromDateValue = mergeDateAndTime(
                dateTimeState?.fromDate ?? now,
                dateTimeState?.fromHour ?? now
            );
            const toDateValue = mergeDateAndTime(
                dateTimeState?.toDate ?? now,
                dateTimeState?.toHour ?? now
            );

            if (fromDateValue.getTime() <= toDateValue.getTime()) {
                return { from: fromDateValue, to: toDateValue };
            }

            return { from: toDateValue, to: fromDateValue };
        }

        const parsedInterval = intervalTimeValue ? new Date(intervalTimeValue) : null;
        const fromDateValue = parsedInterval && !isNaN(parsedInterval.getTime())
            ? parsedInterval
            : now;

        return { from: fromDateValue, to: now };
    }, [checked, isAdvanced, dateTimeState, intervalTimeValue]);

    const sliderRangeLabels = useMemo(() => {
        if (checked && isAdvanced) {
            return {
                from: convertToSystemLocal(selectedRangeDates.from),
                to: convertToSystemLocal(selectedRangeDates.to)
            };
        }

        return {
            from: formattedIntervalDate || convertToSystemLocal(selectedRangeDates.from),
            to: convertToSystemLocal(selectedRangeDates.to)
        };
    }, [checked, isAdvanced, formattedIntervalDate, selectedRangeDates]);

    const spaghettiProgressiveTo = useMemo(() => {
        return progressiveDateLabel || sliderRangeLabels.to || '';
    }, [progressiveDateLabel, sliderRangeLabels.to]);

    const isValidYear = (date: Date) => {
        const year = date.getFullYear();
        return year >= 1000; // blocca 0000, 0001, ecc.
    };

    const handleChangeDate = (e: any) => {
        const { name, value } = e.target;

        if (value && !isValidYear(value)) {
            return; // ignora input
        }

        setDateTimeState((prev: any) => {
            const updated = { ...prev, [name]: value };

            if (name === "toDate" && value && prev.fromDate && value < prev.fromDate) {
                return prev; // ignora update
            }

            if (name === "fromDate" && value) {
                updated.fromHour = mergeDateAndTime(value, prev.fromHour);
            }

            if (name === "toDate" && value) {
                updated.toHour = mergeDateAndTime(value, prev.toHour);
            }

            return updated;
        });

        setChecked(true);
    };

    const handleBlurDate = (e: any) => {
        const { name, value } = e.target;

        if (!value) return;

        setDateTimeState((prev: any) => {
            const updated = { ...prev };

            const newDate = e.target.value;

            if (name === "toDate" && prev.fromDate && newDate < prev.fromDate) {
                updated.toDate = null;
            }

            if (name === "fromDate" && prev.toDate && newDate > prev.toDate) {
                updated.toDate = null;
            }

            return updated;
        });
    };
    const handleBlurTime = (e: any) => {
        const { name, value } = e.target;

        if (!value) return;

        setDateTimeState((prev: any) => {
            const updated = { ...prev };

            const newDate = e.target.value;

            if (name === "toHour" && prev.fromHour && newDate < prev.fromHour) {
                updated.toHour = null;
            }

            if (name === "fromHour" && prev.toHour && newDate > prev.toHour) {
                updated.fromHour = null;
            }

            return updated;
        });
    };

    const handleChangeTime = (e: any) => {
        const { name, value } = e.target;

        setDateTimeState((prev: any) => {
            const updated = { ...prev, [name]: value };

            if (name === "toHour" && value && prev.fromHour && value < prev.fromHour) {
                return prev; // ignora update
            }

            if (name === "fromHour" && value) {
                updated.fromDate = mergeDateAndTime(prev.fromDate, value);
            }

            if (name === "toHour" && value) {
                updated.toDate = mergeDateAndTime(prev.toDate, value);
            }

            return updated;
        });

        setChecked(true);
    };

    const buildUtcRangeForRequests = () => {
        const nowUtc = new Date().toISOString();
        const useCustomRange = checked && isAdvanced;

        if (!useCustomRange) {
            return {
                startDate: intervalTimeValue,
                endDate: nowUtc
            };
        }

        const fromDate = dateTimeState?.fromDate ?? new Date();
        const toDate = dateTimeState?.toDate ?? new Date();
        const fromHour = dateTimeState?.fromHour ?? new Date();
        const toHour = dateTimeState?.toHour ?? new Date();

        const startDateUtc = mergeDateAndTime(fromDate, fromHour).toISOString();
        const endDateUtc = mergeDateAndTime(toDate, toHour).toISOString();

        return {
            startDate: startDateUtc,
            endDate: endDateUtc
        };
    };

    const clearRightPanel = () => {
        setCoordinates([]);
        setInfoDevice([]);
        setShowZone(false);
        setShowZoneMapGeo([]);
        setShowAllPoints(false);
        setShowInfoDevice(false);
        setNoData(true);

        const mapCtx = mapCanvasRef?.current?.getContext("2d");
        const zoneCtx = zoneCanvasRef?.current?.getContext("2d");
        const lineCtx = lineCanvasRef?.current?.getContext("2d");
        const markerCtx = markerCanvasRef?.current?.getContext("2d");

        if (mapCanvasRef?.current) {
            mapCtx?.clearRect(0, 0, mapCanvasRef.current.width, mapCanvasRef.current.height);
        }
        if (zoneCanvasRef?.current) {
            zoneCtx?.clearRect(0, 0, zoneCanvasRef.current.width, zoneCanvasRef.current.height);
        }
        if (lineCanvasRef?.current) {
            lineCtx?.clearRect(0, 0, lineCanvasRef.current.width, lineCanvasRef.current.height);
        }
        if (markerCanvasRef?.current) {
            markerCtx?.clearRect(0, 0, markerCanvasRef.current.width, markerCanvasRef.current.height);
        }
    };

    const isZeroCoordinate = (latitude: any, longitude: any) => {
        return Number(latitude) === 0 && Number(longitude) === 0;
    };

    const filterOutZeroCoordinatesWakeups = (wakeups: any[] = []) => {
        return wakeups.filter((wakeup: any) => {
            const latitude = wakeup?.position?.latitude;
            const longitude = wakeup?.position?.longitude;

            if (latitude === undefined || latitude === null || longitude === undefined || longitude === null) {
                return false;
            }

            return !isZeroCoordinate(latitude, longitude);
        });
    };

    const retrieveWakeups = async (deviceMac: any, mapId?: any, isGrouped?: boolean) => {
        setLoading(true);

        setCoordinates([]);
        setShowColor(false);

        const { startDate, endDate } = buildUtcRangeForRequests();
        setToDateLabel(sliderRangeLabels.to)
        setProgressiveDateLabel('')

        const params: any = {
            startDate,
            endDate
        };

        if (signalsGrouping && isGrouped !== false) {

            if (groupingType === 'near') {
                params.groupNearDistance = groupingValues['near'];
            } else {
                params.groupBy = groupingValues['each'];
            }
            if (averagePositions) {
                params.averagePositions = averagePositions;
            }
        }

        if (mapId) {
            params.mapId = mapId;
        }


        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };

            axios.post(`${SERVER_URL}/wakeup/${deviceMac}/logs/spaghetti`, params, { headers })
                .then((response: any) => {

                    const rawWakeups = response?.data?.data?.wakeUpLogs ?? [];
                    const data = filterOutZeroCoordinatesWakeups(rawWakeups);
                    if (data?.length == 0) {
                        setAnalyzeEnabled(false);
                        setNoData(true);
                        setMessageNoData(true);

                        setTimeout(() => {
                            setMessageNoData(false);
                        }, 3000);

                        setLoading(false)
                        return
                    }

                    setCountWakeups(data?.length);

                    const distance = response?.data?.data?.distance
                    setDistance(distance)

                    const coordinatesList: any = data
                    const mapId: any = response?.data?.data?.mapId

                    let currCoordinates: any = coordinatesList?.map((coord: any) => (
                        [coord.position.latitude, coord.position.longitude, coord.position.color, coord.datetime, coord.lastPositionDatetime, coord.likelihood]
                    ));

                    setCoordinates(prev => [...prev, { coord: [currCoordinates] }]);

                    if (!signalsGrouping) {
                        setInfoDevice(currCoordinates.map(([lat, lon, color, date, lastPositionDatetime, likelihood]: any) => [lon, lat, color, date, lastPositionDatetime, likelihood]))
                    }

                    getMapById(mapId, currCoordinates);

                    setSpaghettiChartMessage(true);
                    setAnalyzeEnabled(true);
                    setNoData(false);
                    setShowZone(false)
                    setShowAllPoints(false)

                    setTimeout(() => {
                        setSpaghettiChartMessage(false);
                    }, 3000);

                    setShowZone(false)

                    setLoading(false)
                }).catch((data) => {
                    if (data?.response.data.errorMessage == 'Error executing the API: Start date must be before end date') {
                        setError(true)
                        if (languageId == 'it-IT') {
                            setErrorMessage("Errore: La data di inizio deve essere prima della data di fine");
                        } else {
                            setErrorMessage("Error: Start date must be before end date");
                        }
                    } else if (data?.response.data.errorMessage == 'Error executing the API: There are no wakeups available for the selected map') {
                        setWarningMessage(true)
                        if (languageId == 'it-IT') {
                            setErrorMessage("Non ci sono wakeup disponibili per la mappa selezionata");
                        } else {
                            setErrorMessage("There are no wakeups available for the selected map");
                        }
                    }
                    if (!error) {
                        setTimeout(() => {
                            setError(false);
                        }, 3000);
                    }
                    if (!warningMessage) {
                        setTimeout(() => {
                            setWarningMessage(false);
                        }, 3000);
                    }
                    clearRightPanel();
                    setLoading(false)
                })
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then(async (accessTokenResponse: any) => {
                // Acquire token silent success
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken)
            })
        }
    }

    const getMapById = async (mapId: any, coordinates?: any) => {

        setLoading(true);

        const commonOperations = (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/map/${mapId}`, { headers })
                .then((res) => {
                    const mapSelected = res?.data.data

                    if(mapSelected.georeferenced != 1) {
                        setMapGeoreferenced(false)
                    }else{
                        toggleGeoStatic ? setMapGeoreferenced(true) : setMapGeoreferenced(false)
                    }
                    

                    renderSelectedMap(mapSelected, coordinates)

                    setSelectedMap(mapSelected)
                    setMapName(mapSelected.name)

                    const mapCtx = mapCanvasRef?.current?.getContext("2d");
                    const mapWidth = mapCanvasRef?.width ?? mapCanvasRef?.current?.width
                    const mapHeight = mapCanvasRef?.height ?? mapCanvasRef?.current?.height
                    mapCtx?.clearRect(0, 0, mapWidth, mapHeight)

                    setLoading(false);
                })
                .catch((error) => {
                    clearRightPanel();
                    setError(true);
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    setLoading(false);
                });
        }

        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    console.log(error);
                });
        }
    }

    const drawAllMarkers = useCallback(() => {

        const markerCanvas = markerCanvasRef.current;

        if (!markerCanvas) return;

        const ctx = markerCanvas.getContext("2d");

        if (!ctx) return;

        if (!showAllPoints) {
            ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
            return;
        }

        const points = toggleGeoStatic ? coordinates[0]?.coord[0] : renderCoordinates;

        if (!points?.length || !mapImageWidth || !mapImageHeight) {
            return;
        }

        const ratio = window.devicePixelRatio || 1;

        const targetWidth = mapImageWidth * ratio;

        const targetHeight = mapImageHeight * ratio;

        if (markerCanvas.width !== targetWidth || markerCanvas.height !== targetHeight) {

            markerCanvas.width = targetWidth;
            markerCanvas.height = targetHeight;

            markerCanvas.style.width = `${mapImageWidth}px`;

            markerCanvas.style.height = `${mapImageHeight}px`;
        }

        ctx.setTransform(1, 0, 0, 1, 0, 0);

        ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
        ctx.scale(ratio, ratio);
        points.forEach((coordinate: any) => {

            let drawX = coordinate.x;
            let drawY = coordinate.y;

            if (toggleGeoStatic) {
                drawX = coordinate[1] * mapImageWidth;
                drawY = mapImageHeight - (coordinate[0] * mapImageHeight);
            }

            const baseX = Math.round(drawX) - 5;
            const baseY = Math.round(drawY);

            ctx.fillStyle = "#198692";
            ctx.lineWidth = 1;
            ctx.strokeStyle = "white";

            ctx.beginPath();
            ctx.arc(baseX, baseY, 8, Math.PI, 0, false);
            ctx.lineTo(baseX, baseY + 7 * 2.2);
            ctx.imageSmoothingEnabled = true;
            ctx.closePath();
            ctx.fill();
            ctx.stroke();
            ctx.save();
            ctx.globalCompositeOperation = "destination-out";

            ctx.beginPath();
            ctx.arc(baseX, baseY, 3, 0, 2 * Math.PI);
            ctx.fill();
            ctx.restore();
        });

    }, [showAllPoints, renderCoordinates, mapImageWidth, mapImageHeight, mapGeoreferenced]);

    const renderSelectedMap = (map: any, coordinates?: any) => {
        const mapCanvas = !mapCanvasRef.current ? document.getElementById('mapCanvas') : mapCanvasRef.current;
        const zoneCanvas = !zoneCanvasRef.current ? document.getElementById('zoneCanvas') : zoneCanvasRef.current;
        const lineCanvas = !lineCanvasRef.current ? document.getElementById('lineCanvas') : lineCanvasRef.current

        const image = new Image();
        image.src = `data:image/jpeg;base64, ${map.image}`;

        image.onload = () => {
            // Check if canvas elements exist before setting properties
            if (!mapCanvas || !zoneCanvas || !lineCanvas) {
                console.error('Canvas elements not found in DOM');
                return;
            }

            mapCanvas.width = zoneCanvas.width = lineCanvas.width = image?.naturalWidth;
            mapCanvas.height = zoneCanvas.height = lineCanvas.height = image?.naturalHeight;

            const mapCtx = mapCanvas.getContext('2d');
            mapCtx.drawImage(image, 0, 0);
            mapCtx.transform(1, 0, 0, -1, 0, mapCanvasRef.current.height)

            const lineCtx = lineCanvas?.getContext("2d");

            coordinates?.forEach((line: any, index: number) => {
                if (index === 0) return; // il primo punto non ha segmento precedente

                const prev = coordinates[index - 1];

                const x1 = Math.round(lineCanvas.width * prev[1]);
                const y1 = Math.round(lineCanvas.height - lineCanvas.height * prev[0]);

                const x2 = Math.round(lineCanvas.width * line[1]);
                const y2 = Math.round(lineCanvas.height - lineCanvas.height * line[0]);

                // decide colore del segmento (preferibilmente del punto attuale)
                const color = showColor ? getLikelihoodColor(line[5]) : "#ff0000";

                lineCtx.beginPath();
                lineCtx.strokeStyle = color;
                lineCtx.lineWidth = 2;

                lineCtx.moveTo(x1, y1);
                lineCtx.lineTo(x2, y2);
                lineCtx.stroke();
            });

            setMapImageWidth(mapCanvas.width)
            setMapImageHeight(mapCanvas.height)

            drawAllMarkers();

        };

        const markerCanvas = markerCanvasRef.current;
        if (!markerCanvas) return;
        const ctx = markerCanvas.getContext("2d");
        ctx.clearRect(0, 0, markerCanvas.width, markerCanvas.height);
    }

    const changeMap = (event: any) => {

        let currMap: any = ''
        if (event?.value != undefined) {
            currMap = event?.value ?? event ?? ''
        } else {
            currMap = event.name ?? event ?? ''
        }

        const currListMaps: any = listMaps
        const isMapGeo = currListMaps?.find((map: any) => map.name == currMap)?.georeferenced
        const booleanIsMapGeo: any = isMapGeo == 0 ? false : true
        localStorage.setItem('mapGeorefenced', booleanIsMapGeo)
        setMapGeoreferenced(booleanIsMapGeo)

        let mapSelected = currListMaps?.find((map: any) => map.name == currMap)
        const updateMapSelected = mapSelected ?? currListMaps?.find((map: any) => map.id == currMap)
        if (updateMapSelected == null) return

        checkResults(selectedDevices[0], updateMapSelected.id);


        //setAnalyzeEnabled(true)
        setSelectedMap(updateMapSelected)
        setMapName(updateMapSelected.name)

        //setShowZone(false)
        //setIsValid(false)

        //getMapById(updateMapSelected?.id)
    }

    const getZoneList = async (mapId: number, isGeoreferenced?: boolean) => {

        if (mapId == null || mapId == undefined) return

        setLoading(true);

        const commonOperations = async (accessToken: any) => {
            const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
            axios.get(`${SERVER_URL}/zone/bymapid/${mapId}?isGeoreferenced=${isGeoreferenced}`, { headers })
                .then((res) => {
                    const listZones = res?.data?.data

                    let zoneCtx: any;
                    if (zoneCanvasRef.current != null) {
                        zoneCtx = zoneCanvasRef?.current?.getContext("2d");
                        zoneCtx.clearRect(0, 0, zoneCanvasRef.width, zoneCanvasRef.height);
                    }

                    listZones?.forEach((zone: any) => {
                        if (!isGeoreferenced) {
                            zoneCtx.beginPath();
                            zoneCtx.fillStyle = zone.color
                            zoneCtx.moveTo(zoneCanvasRef?.current.width * zone.points[0].longitude, zoneCanvasRef?.current.height - zoneCanvasRef?.current.height * zone.points[0].latitude);
                            zone.points.forEach((points: any) => {
                                zoneCtx.lineTo(zoneCanvasRef?.current.width * points.longitude, zoneCanvasRef?.current.height - (zoneCanvasRef?.current.height * points.latitude));
                            });
                            zoneCtx.globalAlpha = 0.4;
                            zoneCtx.fill();
                        } else {
                            setShowZoneMapGeo(listZones)
                        }
                    });
                    setLoading(false);
                })
                .catch((error) => {
                    setLoading(false);
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }
    }

    const Controls = () => {
        const { zoomIn, zoomOut, resetTransform } = useControls();

        return (
            <div className="k-button" style={{ border: 'none', marginTop: '10px' }}>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '10px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.zoom_in', 'NOT_FOUND')}
                    onClick={() => zoomIn()}>+</button>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '47px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.zoom_out', 'NOT_FOUND')}
                    onClick={() => zoomOut()}>-</button>
                <button
                    style={{
                        position: 'absolute',
                        width: '32px',
                        height: '32px',
                        zIndex: 10,
                        top: '0px',
                        left: '84px',
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        cursor: 'pointer'
                    }}
                    title={localizationService.toLanguageString('map.reset_zoom', 'NOT_FOUND')}
                    onClick={() => resetTransform()}>
                    <SvgIcon icon={currentLocation} />
                </button>

            </div>
        );
    };

    useEffect(() => {
        if (focusedItemMac && itemRefs.current[focusedItemMac]) {
            itemRefs.current[focusedItemMac].focus();
        }
    }, [focusedItemMac]);

    const primaryDeviceLabel = selectedDevices?.[0] ?? '';

    const getMapList = async () => {

        const commonOperations = async (accessToken: any) => {
            try {
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                const res = await axios.get(`${SERVER_URL}/map/full/list`, { headers });
                const data = res?.data.data;

                if (!data?.length) {
                    setLoading(false);
                    return;
                }

                const mapsList = data;
                const mapNames = data.map((map: any) => map.name);

                setListMaps(mapsList);
                setListMapNames(mapNames);

            } catch (error) {
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
            }
        };


        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                    //setLoading(false);
                });
        }
    }

    const alignedData = useMemo(() => {
        if (!infoDevice?.length) return [];

        if (toggleGeoStatic) {
            return infoDevice.map((d: any) => ({
                coord: [d[0], d[1]], // [lon, lat]
                date: d[3],          // timestamp
                likelihood: d[5],   // likelihood
            }));
        } else {
            return renderCoordinates.map((coord: any, index: number) => ({
                coord: [coord.x / mapImageWidth, 1 - (coord.y / mapImageHeight)], // normalized [lon, lat]
                date: infoDevice[index]?.[3], // timestamp
                likelihood: infoDevice[index]?.[5], // likelihood
            }));
        }
    }, [infoDevice, renderCoordinates, mapImageWidth, mapImageHeight, toggleGeoStatic]);

    useEffect(() => {
        lastSliderIndexRef.current = -1;
    }, [alignedData]);

    const updateLines = (s: number) => {

        const canvas = lineCanvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);

        const maxIndex = Math.min(s, alignedData.length - 1);
        if (maxIndex < 0) return;

        let previousPoint = alignedData[0];
        if (!previousPoint?.coord) return;

        for (let i = 1; i <= maxIndex; i++) {
            const currentPoint = alignedData[i];
            if (!currentPoint?.coord || !previousPoint?.coord) continue;

            const startX = canvas.width * previousPoint.coord[0];
            const startY = canvas.height - canvas.height * previousPoint.coord[1];
            const endX = canvas.width * currentPoint.coord[0];
            const endY = canvas.height - canvas.height * currentPoint.coord[1];

            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.strokeStyle = showColor ? getLikelihoodColor(currentPoint.likelihood) : "#ff0000";
            ctx.lineWidth = 2;
            ctx.stroke();

            previousPoint = currentPoint;
        }

        const markerCanvas = markerCanvasRef.current;
        if (!markerCanvas) return;

        const ctxMarker = markerCanvas.getContext("2d");
        if (!ctxMarker) return;

        ctxMarker.clearRect(0, 0, markerCanvas.width, markerCanvas.height);

        const current = alignedData[s]?.coord;
        if (!current) return;

        const ratio = window.devicePixelRatio || 1;
        const targetWidth = mapImageWidth * ratio;
        const targetHeight = mapImageHeight * ratio;
        if (
            markerCanvasSizeRef.current.width !== targetWidth ||
            markerCanvasSizeRef.current.height !== targetHeight
        ) {
            markerCanvas.width = targetWidth;
            markerCanvas.height = targetHeight;
            markerCanvas.style.width = `${mapImageWidth}px`;
            markerCanvas.style.height = `${mapImageHeight}px`;
            markerCanvasSizeRef.current = { width: targetWidth, height: targetHeight, ratio };
        }

        ctxMarker.setTransform(1, 0, 0, 1, 0, 0);
        ctxMarker.scale(ratio, ratio);

        const x = current[0] * mapImageWidth;
        const y = mapImageHeight - current[1] * mapImageHeight;
        const baseX = Math.round(x) - 5;
        const baseY = Math.round(y) - 15;

        ctxMarker.fillStyle = "#198692";
        ctxMarker.strokeStyle = "white";
        ctxMarker.lineWidth = 1;

        ctxMarker.beginPath();
        ctxMarker.arc(baseX, baseY, 8, Math.PI, 0, false);
        ctxMarker.lineTo(baseX, baseY + 7 * 2.2);
        ctxMarker.closePath();
        ctxMarker.fill();
        ctxMarker.stroke();

        ctxMarker.save();
        ctxMarker.globalCompositeOperation = "destination-out";
        ctxMarker.beginPath();
        ctxMarker.arc(baseX, baseY, 3, 0, 2 * Math.PI);
        ctxMarker.fill();
        ctxMarker.restore();
    };

    const toWakeupTimestamp = (value?: string | number | Date | null): number | null => {
        if (value === null || value === undefined) {
            return null;
        }

        if (value instanceof Date) {
            const ts = value.getTime();
            return Number.isFinite(ts) ? ts : null;
        }

        if (typeof value === 'number') {
            return Number.isFinite(value) ? value : null;
        }

        if (typeof value !== 'string') {
            return null;
        }

        const raw = value.trim();
        if (!raw) {
            return null;
        }

        const hasExplicitTimezone = /([zZ]|[+-]\d{2}:?\d{2})$/.test(raw);

        // Native parse only when timezone is explicit.
        if (hasExplicitTimezone) {
            const direct = new Date(raw).getTime();
            if (Number.isFinite(direct)) {
                return direct;
            }
        }

        // "YYYY-MM-DD HH:mm:ss" or "YYYY-MM-DDTHH:mm:ss" without timezone: treat as UTC.
        const ymdMatch = raw.replace(',', '').match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
        if (ymdMatch) {
            const [, yyyy, mm, dd, hh = '0', min = '0', ss = '0'] = ymdMatch;
            const parsedUtc = Date.UTC(
                Number(yyyy),
                Number(mm) - 1,
                Number(dd),
                Number(hh),
                Number(min),
                Number(ss),
                0
            );

            return Number.isFinite(parsedUtc) ? parsedUtc : null;
        }

        // "DD/MM/YYYY HH:mm:ss" or "DD/MM/YYYY, HH:mm:ss" without timezone: treat as UTC.
        const dmyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
        if (dmyMatch) {
            const [, dd, mm, yyyy, hh = '0', min = '0', ss = '0'] = dmyMatch;
            const parsedUtc = Date.UTC(
                Number(yyyy),
                Number(mm) - 1,
                Number(dd),
                Number(hh),
                Number(min),
                Number(ss),
                0
            );

            return Number.isFinite(parsedUtc) ? parsedUtc : null;
        }

        const fallback = new Date(raw).getTime();
        if (Number.isFinite(fallback)) {
            return fallback;
        }

        return null;
    };

    const bindMarkerTooltip = (point: any, label: string) => {
        if (!point?.coord) {
            markerHoverDataRef.current = null;
            setShowTooltip(false);
            return;
        }
        markerHoverDataRef.current = { point, label };
    };

    const applySliderValue = (percent: number) => {
        if (!alignedData.length) return;

        const ratio = (Math.max(timeSliderMin, Math.min(timeSliderMax, percent)) - timeSliderMin) /
            (timeSliderMax - timeSliderMin);
        const targetIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round(ratio * (alignedData.length - 1))));
        const currentPoint = alignedData[targetIndex];
        if (!currentPoint) return;

        const parsedPointTimestamp = toWakeupTimestamp(currentPoint?.date ?? null);
        const readableDate = parsedPointTimestamp === null
            ? currentPoint.date
            : convertToSystemLocal(new Date(parsedPointTimestamp));

        setProgressiveDateLabel(readableDate);
        if (lastSliderIndexRef.current !== targetIndex) {
            lastSliderIndexRef.current = targetIndex;
            updateLines(targetIndex);
        }
        bindMarkerTooltip(currentPoint, readableDate);
    };

    const handleSliderChange = (event: any) => {
        if (!alignedData.length) return;

        const rawValue = Number(event?.value ?? event?.target?.value ?? event);
        if (Number.isNaN(rawValue)) return;

        const percent = Math.max(timeSliderMin, Math.min(timeSliderMax, rawValue));
        setStep(percent);

        if (sliderFrameRef.current) {
            cancelAnimationFrame(sliderFrameRef.current);
        }

        sliderFrameRef.current = requestAnimationFrame(() => {
            sliderFrameRef.current = null;
            applySliderValue(percent);
        });
    };

    const handleGeoSliderDateChange = useCallback((dateLabel?: string) => {
        if (!dateLabel || typeof dateLabel !== 'string') {
            return;
        }

        const parsedPointTimestamp = toWakeupTimestamp(dateLabel);
        const readableDate = parsedPointTimestamp === null
            ? dateLabel
            : convertToSystemLocal(new Date(parsedPointTimestamp));

        setProgressiveDateLabel(readableDate || sliderRangeLabels.to || '');
    }, [sliderRangeLabels.to]);

    useEffect(() => {
        if (!alignedData.length) {
            return;
        }

        applySliderValue(step);
    }, [alignedData]);


    useEffect(() => {
        drawAllMarkers();
    }, [drawAllMarkers]);

    const [transformState, setTransformState] = useState<any>([])
    const [tooltipContent, setTooltipContent] = useState("");
    const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
    const [showTooltip, setShowTooltip] = useState(false);
    const [lastMouseEvent, setLastMouseEvent] = useState(null);
    const transformContentRef = useRef<HTMLDivElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (lastMouseEvent) {
            handleDeviceTooltipStaticMap(lastMouseEvent);
        }
    }, [transformState]);

    //single tooltip 
    useEffect(() => {
        if (showTooltip) return;

        const canvas: HTMLCanvasElement | null = markerCanvasRef.current;
        if (!canvas) return;

        const handleMove = (evt: MouseEvent) => {
            if (!transformContentRef.current) return;
            const hoverData = markerHoverDataRef.current;
            if (!hoverData || !mapImageWidth || !mapImageHeight) {
                setShowTooltip(false);
                return;
            }

            const { point, label } = hoverData;
            if (!point?.coord) {
                setShowTooltip(false);
                return;
            }

            const [lon, lat] = point.coord;
            const coordX = lon * mapImageWidth;
            const coordY = mapImageHeight - lat * mapImageHeight;

            const rect = transformContentRef.current.getBoundingClientRect();
            const { scale } = transformState;
            const mouseX = (evt.clientX - rect.left) / scale;
            const mouseY = (evt.clientY - rect.top) / scale;
            const distance = Math.hypot(mouseX - coordX, mouseY - coordY);


            if (distance <= 12) {
                const tooltipHtml = `
                    <div class='popupMarkerLineChart'>
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('configuration.analysis.path_history.mac', 'NOT_FOUND')}:</span>
                            <span>${primaryDeviceLabel}</span>
                        </span>
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>D:</span>
                            <span>${label}</span>
                        </span>
                    </div>
                `;
                setTooltipContent(tooltipHtml);
                setTooltipPos({ x: evt.clientX + 10, y: evt.clientY + 10 });
                setShowTooltip(true);
            } else {
                setShowTooltip(false);
            }
        };

        const handleLeave = () => setShowTooltip(false);

        canvas.addEventListener('mousemove', handleMove);
        canvas.addEventListener('mouseleave', handleLeave);

        return () => {
            canvas.removeEventListener('mousemove', handleMove);
            canvas.removeEventListener('mouseleave', handleLeave);
            if (sliderFrameRef.current) {
                cancelAnimationFrame(sliderFrameRef.current);
            }
        };
    }, [mapImageWidth, mapImageHeight, primaryDeviceLabel, transformState]);

    //tooltips
    const handleDeviceTooltipStaticMap = (e: any) => {

        if (!showAllPoints) return;

        setLastMouseEvent(e);
        if (!transformContentRef.current) return;
        if (!mapImageWidth || !mapImageHeight) return;
        if (coordinates.length === 0) return;

        const { scale } = transformState;
        const rect = transformContentRef.current.getBoundingClientRect();

        // Coordinate del mouse relative all'immagine, corrette per lo zoom
        const mouseX = (e.clientX - rect.left) / scale;
        const mouseY = (e.clientY - rect.top) / scale;

        let tooltipStr = "";
        let found = false;

        const normalizedCoordinates = toggleGeoStatic ? coordinates[0]?.coord[0] : renderCoordinates;

        normalizedCoordinates?.forEach((coordinate: any, index: number) => {
            const radius = 10;

            const dateStr = coordinate[3] || infoDevice[index]?.[3] || '';
            const [datePart, timePart] = dateStr.split(" ");
            const [day, month, year] = datePart.split("/");
            const [hours, minutes, seconds] = timePart.split(":");

            const utcDate = new Date(Date.UTC(
                year,
                month - 1,
                day,
                hours,
                minutes,
                seconds
            ));

            const localDate = new Date(utcDate);

            const lat = toggleGeoStatic ? coordinate[0] : coordinate.y / mapImageHeight;
            const lon = toggleGeoStatic ? coordinate[1] : coordinate.x / mapImageWidth;

            // Converti le coordinate normalizzate (0â€“1) in pixel reali
            const coordX = lon * mapImageWidth;
            const coordY = mapImageHeight - lat * mapImageHeight;

            const distance = Math.hypot(mouseX - coordX, mouseY - coordY);

            if (distance <= radius && showAllPoints) {
                tooltipStr = `
                <div class='popupMarkerLineChart'>
                    <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>
                            ${localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:
                        </span>
                        <span>${countWakeups}</span>
                    </span>
                    <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('configuration.analysis.path_history.mac', 'NOT_FOUND')}:</span>
                        <span>${selectedDevices}</span>
                    </span>
                    <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>
                            ${localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}:
                        </span>
                        <span>${infoDevice[index]?.[5].toFixed(2)}</span>
                    </span>
                    <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>
                            D:
                        </span>
                        <span>
                            ${localDate.toLocaleString()}
                        </span>
                    </span>

                    <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>
                            LD:
                        </span>
                        <span>${coordinate[4]}</span>
                    </span>
                </div>`;
                found = true;
            }
        });

        if (found) {
            setTooltipContent(tooltipStr);
            setTooltipPos({ x: e.clientX + 10, y: e.clientY + 10 });
            setShowTooltip(true);
        } else {
            setShowTooltip(false);
        }
    };

    const toDateSafe = (value: any) => {
        if (!value) return null;

        if (value instanceof Date) return value; // giÃ  una Date

        if (typeof value === 'string') {
            // tenta di interpretare la stringa DD/MM/YYYY HH:mm:ss
            const [datePart, timePart] = value.split(' ');
            if (!datePart) return null;
            const [day, month, year]: any = datePart.replace(',', '').split('/');
            const [hour = 0, minute = 0, second = 0]: any = (timePart || '').split(':');

            const d: any = new Date(year, month - 1, day, hour, minute, second);
            return isNaN(d.getTime()) ? null : d;
        }

        return null;
    };

    const [filter, setFilter] = useState("");
    const [filteredDevices, setFilteredDevices] = useState(devicesWithSelection);

    useEffect(() => {
        const safeDevices = (devicesWithSelection || [])
            .filter((d: any) => d && typeof d.mac === "string")
            .map((d: any) => ({
                ...d,
                displayName: d.name && d.name.trim() !== "" ? d.name : d.mac,
            }));

        if (!filter) {
            setFilteredDevices(safeDevices);
        } else {
            const lower = filter.toLowerCase();
            setFilteredDevices(
                safeDevices.filter((d: any) => {
                    const name = (d.name || "").toLowerCase();
                    const mac = d.mac.toLowerCase();
                    return name.includes(lower) || mac.includes(lower);
                })
            );
        }
    }, [filter, devicesWithSelection]);

    const handleChange = useCallback(
        (e: RadioButtonChangeEvent) => {
            setGroupingType(e.value);
        },
        [setGroupingType]
    );

    const handleValueChange = (type: 'near' | 'each', value: number) => {
        setGroupingValues((prev) => ({ ...prev, [type]: value }));
    };

    const checkResults = async (deviceMac: any, mapId?: any, isGrouped?: boolean) => {

        setLoading(true);

        setCoordinates([]);
        setShowColor(false);

        const { startDate, endDate } = buildUtcRangeForRequests();
        setToDateLabel(sliderRangeLabels.to)
        setProgressiveDateLabel('')

        const commonOperations = async (accessToken: any) => {
            try {
                const params = {
                    startDate,
                    endDate,
                    mac: [deviceMac]
                };
                const headers = { 'Authorization': `Bearer ${accessToken}`, 'Language': languageId };
                const res = await axios.post(`${SERVER_URL}/WakeUp/count`, params, { headers });
                const totalWakeupsCount = Number(res?.data?.data ?? 0);

                let data = totalWakeupsCount;
                if (totalWakeupsCount > 0) {
                    const wakeupParams: any = {
                        startDate,
                        endDate
                    };

                    if (signalsGrouping && isGrouped !== false) {
                        if (groupingType === 'near') {
                            wakeupParams.groupNearDistance = groupingValues['near'];
                        } else {
                            wakeupParams.groupBy = groupingValues['each'];
                        }

                        if (averagePositions) {
                            wakeupParams.averagePositions = averagePositions;
                        }
                    }

                    if (mapId) {
                        wakeupParams.mapId = mapId;
                    }

                    const wakeupsResponse = await axios.post(`${SERVER_URL}/wakeup/${deviceMac}/logs/spaghetti`, wakeupParams, { headers });
                    const wakeupsList = wakeupsResponse?.data?.data?.wakeUpLogs ?? [];
                    data = filterOutZeroCoordinatesWakeups(wakeupsList).length;
                }

                setCountWakeups(data);
                if (data <= COUNTER_MAX_POINTS) {
                    if (data == 0) {
                        setIsValid(false)
                        setLoading(false);
                        setMessageNoData(true);

                        setTimeout(() => {
                            setMessageNoData(false);
                        }, 3000);
                    } else {
                        setIsValid(true)
                        retrieveWakeups(deviceMac, mapId, isGrouped);
                    }
                } else {
                    setIsValid(false)
                    setChecked(false)
                    setMessageTooData(true);
                    setAnalyzeEnabled(false);
                    setTimeout(() => {
                        setMessageTooData(false);
                    }, 3000);
                    setLoading(false);
                    return;
                }

            } catch (error: any) {
                const errorMessage = error?.response?.data?.errorMessage || '';
                if (errorMessage.includes('Start date must be before end date')) {
                    setError(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Errore: La data di inizio deve essere prima della data di fine");
                    } else {
                        setErrorMessage("Error: Start date must be before end date");
                    }
                } else if (errorMessage.includes('There are no wakeups available for the selected map')) {
                    setWarningMessage(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Non ci sono wakeup disponibili per la mappa selezionata");
                    } else {
                        setErrorMessage("There are no wakeups available for the selected map");
                    }
                } else {
                    setError(true);
                    if (languageId == 'it-IT') {
                        setErrorMessage("Errore sconosciuto durante il controllo dei risultati");
                    } else {
                        setErrorMessage("Unknown error during results check");
                    }
                }
                if (error instanceof InteractionRequiredAuthError) {
                    instance.acquireTokenRedirect(accessTokenRequest);
                }
                setTimeout(() => {
                    setError(false);
                }, 3000);
                if (!warningMessage) {
                    setTimeout(() => {
                        setWarningMessage(false);
                    }, 3000);
                }
                setLoading(false);
                return;
            }
        };


        if (localStorage.getItem('accessToken') != null) {
            let accessToken = localStorage.getItem('accessToken');
            commonOperations(accessToken);
        } else {
            await instance.initialize();
            await instance.acquireTokenSilent(accessTokenRequest).then((accessTokenResponse: any) => {
                let accessToken = accessTokenResponse.accessToken;
                commonOperations(accessToken);
            })
                .catch((error: any) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        instance.acquireTokenRedirect(accessTokenRequest);
                    }
                });
        }

    }

    // Usa una chiave stabile basata sul numero di coordinate per evitare ricalcoli inutili
    const coordsLength = coordinates[0]?.coord[0]?.length || 0;

    const dataChart = useMemo(() => {
        const colorCount = { red: 0, orange: 0, yellow: 0, green: 0, lightgreen: 0 };

        const coordData = coordinates[0]?.coord[0];
        if (!coordData) {
            return [
                { category: localizationService.toLanguageString('configuration.analysis.path_history.very_low', 'Very low'), value: 0, color: "#ff0000" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.low', 'Low'), value: 0, color: "orange" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.medium', 'Medium'), value: 0, color: "yellow" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.good', 'Good'), value: 0, color: "green" },
                { category: localizationService.toLanguageString('configuration.analysis.path_history.optimal', 'Optimal'), value: 0, color: "lightgreen" },
            ];
        }

        coordData.forEach(([, , , , , likelihood]: any) => {
            const color = getLikelihoodColor(likelihood);
            if (colorCount[color] !== undefined) colorCount[color]++;
        });

        return [
            { category: localizationService.toLanguageString('configuration.analysis.path_history.very_low', 'Very low'), value: colorCount.red, color: "#ff0000" },
            { category: localizationService.toLanguageString('configuration.analysis.path_history.low', 'Low'), value: colorCount.orange, color: "orange" },
            { category: localizationService.toLanguageString('configuration.analysis.path_history.medium', 'Medium'), value: colorCount.yellow, color: "yellow" },
            { category: localizationService.toLanguageString('configuration.analysis.path_history.good', 'Good'), value: colorCount.green, color: "green" },
            { category: localizationService.toLanguageString('configuration.analysis.path_history.optimal', 'Optimal'), value: colorCount.lightgreen, color: "lightgreen" },
        ];
    }, [coordsLength]);

    useEffect(() => {
        const hasCoordinates = Boolean(coordinates[0]?.coord[0]?.length);

        if (isValid && selectedMap && hasCoordinates) {
            renderSelectedMap(selectedMap, coordinates[0]?.coord[0]);
        }
    }, [showColor, isValid, selectedMap, coordinates]);

    useEffect(() => {
        if (showZone && selectedMap) {
            getZoneList(selectedMap.id, mapGeoreferenced);
        }
    }, [showZone, selectedMap, mapGeoreferenced, showColor]);

    useEffect(() => {
        setToDateLabel(sliderRangeLabels.to || '');
        setProgressiveDateLabel('');
    }, [sliderRangeLabels.to]);

    const refreshDevices = () => {
        setMapName([])
        setFocusedItemMac(null)
        setAnalyzeEnabled(false)
        setIntervalTimeValue(null)
        setIntervalTimeName(null)
        setChecked(false)
        setIsValid(false)
        setSelectedDevices([])
        getDeviceList(true, checkedOnlyBound)
        setIsAdvanced(false)
    }

    const isMapGeoreferencedFlag = (value: any) => {
        return value === true || value === 1 || value === '1';
    };

    const convertGeoPointsToCanvasPoints = useCallback((geoPoints: any[] = [], mapInfo?: any) => {

        const canvasWidth = lineCanvasRef.current?.width || mapImageWidth || 0;

        const canvasHeight = lineCanvasRef.current?.height || mapImageHeight || 0;

        if (!canvasWidth || !canvasHeight || !geoPoints.length || !mapInfo) {
            return [];
        }

        const { botLeft, botRight, topLeft } = mapInfo;

        if (!botLeft || !botRight || !topLeft) {
            return [];
        }

        const lonSpan = Number(botRight.longitude) - Number(botLeft.longitude);

        const latSpan = Number(topLeft.latitude) - Number(botLeft.latitude);

        if (!lonSpan || !latSpan) {
            return [];
        }

        return geoPoints.map((point: any) => {

            const longitude = Number(point[1]);
            const latitude = Number(point[0]);

            const x = canvasWidth * (longitude - Number(botLeft.longitude)) / lonSpan;

            const y = canvasHeight * (Number(topLeft.latitude) - latitude) / latSpan;

            return { x, y, raw: point };
        });
    }, [mapImageWidth, mapImageHeight]);

    const onToggleGeoStatic = async (e: any) => {

        const toggleValue: any = e.value;

        localStorage.setItem('onToggleGeoStatic', JSON.stringify(toggleValue));

        const mapSupportsGeo = isMapGeoreferencedFlag(selectedMap?.georeferenced);

        const shouldShowGeo = Boolean(mapSupportsGeo && toggleValue);

        localStorage.setItem('mapGeoreferenced', JSON.stringify(shouldShowGeo));

        setMapGeoreferenced(shouldShowGeo);
        setToggleGeoStatic(toggleValue);

        /**
         * GEO -> STATIC
         */
        if (!shouldShowGeo) {

            const coords = coordinates?.[0]?.coord?.[0];

            if (coords?.length) {

                const converted = convertGeoPointsToCanvasPoints(coords, selectedMap);

                const lineCtx = lineCanvasRef?.current?.getContext("2d");

                if (lineCtx && lineCanvasRef.current) {

                    lineCtx.clearRect(0, 0, lineCanvasRef.current.width, lineCanvasRef.current.height);

                    converted.forEach((point: any, index: number) => {

                        if (index === 0) return;

                        const prev = converted[index - 1];

                        const color = showColor ? getLikelihoodColor(point.raw[5]) : "#ff0000";

                        lineCtx.beginPath();
                        lineCtx.strokeStyle = color;
                        lineCtx.lineWidth = 2;

                        lineCtx.moveTo(prev.x, prev.y);
                        lineCtx.lineTo(point.x, point.y);

                        lineCtx.stroke();
                    });
                }
            }
            return;
        }

        /**
         * STATIC -> GEO
         */
        if (toggleValue) {
            drawAllMarkers();
            return;
        }
    };

    useEffect(() => {

        const coords = coordinates?.[0]?.coord?.[0];

        if (!coords?.length) return;

        if (toggleGeoStatic) {

            setRenderCoordinates(coords.map((point: any) => ({
                x: point[1],
                y: point[0],
                raw: point
            })));

            return;
        }

        const converted = convertGeoPointsToCanvasPoints(coords, selectedMap);

        setRenderCoordinates(converted);

    }, [mapGeoreferenced, coordinates, selectedMap, showColor]);

    return (
        <>
            {loading && loadingPanel}
            {devicesList?.length == 0 || devicesList == undefined &&
                <div style={{ textAlign: 'center', padding: '20px', border: '1px dashed #ccc' }}>
                    <p>{localizationService.toLanguageString('configuration.devices.no_data', 'NOT_FOUND')}</p>
                </div>
            }
            {devicesList?.length > 0 &&
                <div
                    style={{
                        display: 'flex',
                        height: '100%'
                    }}
                >
                    <NotificationGroup
                        style={{
                            right: 0,
                            bottom: 50,
                            zIndex: 1200,
                            alignItems: "flex-start",
                            flexWrap: "wrap-reverse",
                        }}
                    >
                        <Fade enter={true} exit={true}>
                            {spaghettiChartMessage && (
                                <Notification
                                    type={{ style: "success", icon: true }}
                                    closable={true}
                                    onClose={() => setSpaghettiChartMessage(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.map_loaded', 'NOT_FOUND')}</span>
                                </Notification>
                            )}
                            {messageNoData &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageNoData(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.no_data', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {messageTooData &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setMessageTooData(false)}
                                >
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.too_data', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {error &&
                                <Notification
                                    type={{ style: "error", icon: true }}
                                    closable={true}
                                    onClose={() => setError(false)}
                                >
                                    <span>{errorMessage}</span>
                                    <span>{localizationService.toLanguageString('configuration.analysis.path_history.no_data_map', 'NOT_FOUND')}</span>
                                </Notification>
                            }
                            {warningMessage &&
                                <Notification
                                    type={{ style: "warning", icon: true }}
                                    closable={true}
                                    onClose={() => setWarningMessage(false)}
                                >
                                    <span>{errorMessage}</span>
                                </Notification>
                            }
                        </Fade>
                    </NotificationGroup>

                    {/* left panel */}
                    <div
                        style={{
                            background: "#F7F7F7",
                            padding: "0",
                            minWidth: '420px',
                            overflowY: "auto"
                        }}
                    >
                        <div
                            className="k-listview-item row p-2 align-middle"
                            style={{ margin: '0 auto' }}
                        >
                            <MultiSelect
                                data={filteredDevices}
                                dataItemKey="mac"
                                textField="displayName"
                                autoClose={true}
                                filterable={true}
                                onFilterChange={(e) => {
                                    setFilter(e.filter?.value || "")
                                    onFilterChange(e.filter?.value || "")
                                }}

                                value={devicesWithSelection
                                    .filter((d: any) => d?.selected && d?.mac)
                                    .map((d: any) => ({
                                        ...d,
                                        displayName: d.name && d.name.trim() !== "" ? d.name : d.mac,
                                    }))
                                }

                                onChange={(e: any) => {
                                    const newSelection = e.value || [];
                                    const newMacs = newSelection
                                        .map((d: any) => (typeof d === 'string' ? d : d?.mac))
                                        .filter((mac: any): mac is string => typeof mac === 'string' && mac.length > 0);

                                    const prevSelected = selectedDevices;

                                    if (newMacs.length > 1) {
                                        return;
                                    }

                                    const added = newMacs.find((mac: string) => !prevSelected.includes(mac));
                                    const removed = prevSelected.find((mac: string) => !newMacs.includes(mac));

                                    if (added && prevSelected.length >= 1) {
                                        return;
                                    }

                                    if (added) {
                                        toggleDeviceSelection(added);
                                    } else if (removed) {
                                        toggleDeviceSelection(removed);
                                    }
                                }}

                                itemRender={(li: any, itemProps: any) => {
                                    const device = itemProps.dataItem;
                                    if (!device || !device.mac) return li;

                                    const isSelected = device.selected;
                                    const isFocused = focusedItemMac === device.mac;
                                    const hasName = !!device.name && device.name.trim() !== "";

                                    return React.cloneElement(li, {
                                        ...li.props,
                                        style: {
                                            ...(li.props.style || {}),
                                            background: isSelected
                                                ? "#198692"
                                                : isFocused
                                                    ? "#198692"
                                                    : "#F7F7F7",
                                            color: isSelected || isFocused ? "#fff" : "#018CA5",
                                            fontSize: "12px",
                                            textTransform: "uppercase",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            cursor: "pointer",
                                            borderBottom: "1px solid #ccc",
                                        },
                                        children: hasName ? (
                                            <>
                                                <span><strong>{device.name}</strong></span>
                                                <span
                                                    style={{
                                                        color: isSelected || isFocused ? "#fff" : "#018CA5",
                                                    }}
                                                >
                                                    ({device.mac})
                                                </span>
                                            </>
                                        ) : (
                                            <span
                                                style={{
                                                    color: isSelected || isFocused ? "#fff" : "#018CA5",
                                                    fontWeight: "bold",
                                                }}
                                            >
                                                {device.mac}
                                            </span>
                                        ),
                                    });
                                }}

                                tagRender={(tagData: any, li) => {
                                    const device = tagData.dataItem;
                                    if (!device) return li;
                                    const label =
                                        device.name && device.name.trim() !== ""
                                            ? `${device.name} (${device.mac})`
                                            : device.mac;
                                    return (
                                        <span
                                            className="my-ms-tag"
                                            style={{
                                                background: "#198692",
                                                color: "#fff",
                                                borderRadius: "2px",
                                                padding: "2px 6px",
                                                margin: "2px",
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: "4px",
                                                fontSize: "10px"
                                            }}
                                        >
                                            {label}
                                        </span>
                                    );
                                }}

                                placeholder={localizationService.toLanguageString('configuration.analysis.path_history.select_device', 'NOT_FOUND')}
                            />

                            <div style={{ margin: '10px 0px 0px 0px' }}>

                                {/*only bound, refresh, check, analysis*/}
                                <div style={{ display: 'flex' }}>

                                    {/*onlyBound*/}
                                    <label htmlFor="mySwitch" style={{ width: '285px' }}>
                                        <span style={{ margin: '0px 15px 0px 0px' }}>
                                            {localizationService.toLanguageString('configuration.analysis.path_history.only_binded', 'NOT_FOUND')}
                                        </span>
                                        <Switch
                                            id="mySwitch"
                                            onChange={switchOnlyBound}
                                            checked={checkedOnlyBound}
                                            size={'large'}
                                        />
                                    </label>

                                    <Button
                                        title={localizationService.toLanguageString('configuration.analysis.path_history.analyze', 'NOT_FOUND')}
                                        className='analysis'
                                        disabled={!checked}
                                        onClick={async () => {
                                            for (const device of selectedDevices) {
                                                await checkResults(device);
                                            }
                                        }}
                                    >
                                        <Avatar
                                            className="analysis"
                                        >
                                            {/*<SvgIcon icon={optimizationIcon} style={{ zoom: '1.5', verticalAlign: 'baseline', margin: '10px' }} />*/}
                                            <SvgIcon icon={chartScatterSmoothLinesMarkersIcon} style={{ verticalAlign: 'baseline', margin: '10px' }} />
                                        </Avatar>
                                    </Button>

                                    {/* refresh device list button */}
                                    <Button
                                        className="plusParam"
                                        style={{ marginTop: '0px' }}
                                        onClick={() => {
                                            refreshDevices();
                                        }}
                                        title={localizationService.toLanguageString('configuration.analysis.path_history.refresh_list', 'NOT_FOUND')}
                                    >
                                        <Avatar
                                            className="plusParam"
                                        >
                                            <SvgIcon icon={arrowRotateCwIcon} style={{ margin: '8px' }} />
                                        </Avatar>
                                    </Button>

                                </div>

                                <div style={{ fontSize: '16px', opacity: `${selectedDevices.length == 0 ? '0.5' : '1'}` }}>
                                    {localizationService.toLanguageString('configuration.analysis.path_history.interval_time', 'NOT_FOUND')}
                                </div>

                                <DropDownList
                                    style={{
                                        width: "180px",
                                        opacity: `${selectedDevices.length == 0 ? '0.5' : '1'}`
                                    }}
                                    data={intervalTimeList.data}
                                    textField="name"
                                    value={intervalTimeName}
                                    disabled={selectedDevices.length == 0}
                                    onChange={onChangeIntervalTime}
                                />

                                {
                                    //advanced timeslot
                                    isAdvanced &&
                                    <div style={{ display: 'grid', width: '350px' }}>
                                        <div>
                                            <DatePicker
                                                name="fromDate"
                                                format="dd/MM/yyyy"
                                                validityStyles={false}
                                                label={localizationService.toLanguageString('configuration.analysis.path_history.from_date', 'NOT_FOUND')}
                                                value={dateTimeState.fromDate}
                                                width={150}
                                                required={true}
                                                onChange={handleChangeDate}
                                                onBlur={handleBlurDate}
                                                validationMessage={
                                                    dateTimeState.fromDate === null
                                                        ? `${localizationService.toLanguageString('configuration.analysis.path_history.from_date', 'NOT_FOUND')}`
                                                        : ""
                                                }
                                            />

                                            <DatePicker
                                                name="toDate"
                                                format="dd/MM/yyyy"
                                                style={{ marginLeft: '20px' }}
                                                validityStyles={false}
                                                label={localizationService.toLanguageString('configuration.analysis.path_history.to_date', 'NOT_FOUND')}
                                                value={dateTimeState.toDate}
                                                width={150}
                                                required={true}
                                                min={dateTimeState.fromDate || undefined}
                                                onChange={handleChangeDate}
                                                onBlur={handleBlurDate}
                                                validationMessage={
                                                    dateTimeState.toDate === null
                                                        ? `${localizationService.toLanguageString('configuration.analysis.path_history.to_date', 'NOT_FOUND')}`
                                                        : ""
                                                }
                                            />
                                        </div>

                                        <div style={{ display: 'flex', margin: '10px 0 10px 0' }}>
                                            <div>
                                                <TimePicker
                                                    format="HH:mm:ss"
                                                    name="fromHour"
                                                    validityStyles={false}
                                                    value={dateTimeState.fromHour}
                                                    width={150}
                                                    required={true}
                                                    onChange={handleChangeTime}
                                                    onBlur={handleBlurTime}
                                                    validationMessage={
                                                        dateTimeState.fromHour === null
                                                            ? `${localizationService.toLanguageString('configuration.analysis.path_history.from_hour', 'NOT_FOUND')}`
                                                            : ""
                                                    }
                                                />
                                            </div>
                                            <div style={{ marginLeft: '20px' }}>
                                                <TimePicker
                                                    format="HH:mm:ss"
                                                    name="toHour"
                                                    validityStyles={false}
                                                    value={dateTimeState.toHour}
                                                    width={150}
                                                    required={true}
                                                    min={dateTimeState.fromHour || undefined}
                                                    valid={(() => {
                                                        const { fromHour, toHour } = dateTimeState || {};

                                                        const from = toDateSafe(fromHour);
                                                        const to = toDateSafe(toHour);

                                                        if (!from || !to) return false;
                                                        return from.getTime() < to.getTime();
                                                    })()}
                                                    onChange={handleChangeTime}
                                                    onBlur={handleBlurTime}
                                                    validationMessage={
                                                        dateTimeState.toHour === null
                                                            ? `${localizationService.toLanguageString('configuration.analysis.path_history.to_hour', 'NOT_FOUND')}`
                                                            : `${localizationService.toLanguageString('configuration.analysis.path_history.to_hour', 'NOT_FOUND')}`
                                                    }
                                                />
                                            </div>
                                        </div>
                                    </div>
                                }

                                {/*switch buttons*/}
                                <div style={{ display: 'flex', textAlign: 'center' }}>

                                    {/*show zones*/}
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={{ marginRight: '5px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}>
                                            {localizationService.toLanguageString('configuration.analysis.path_history.show_zones', 'NOT_FOUND')}
                                        </span>
                                        <Switch
                                            onChange={() => {
                                                setShowZone(!showZone)
                                                if (mapGeoreferenced) {
                                                    setShowZoneMapGeo([])
                                                } else {
                                                    const zoneCtx = zoneCanvasRef?.current?.getContext("2d");
                                                    const zoneWidth = zoneCanvasRef?.width ?? zoneCanvasRef?.current?.width
                                                    const zoneHeight = zoneCanvasRef?.height ?? zoneCanvasRef?.current?.height
                                                    zoneCtx?.clearRect(0, 0, zoneWidth, zoneHeight)
                                                }
                                            }}
                                            checked={showZone}
                                            disabled={!analyzeEnabled || noData || selectedDevices.length == 0}
                                            size={'large'}
                                        />
                                    </div>

                                    {/*show all markers*/}
                                    <div style={{ marginTop: '10px' }}>
                                        <span style={{ marginRight: '5px', marginLeft: '10px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}>
                                            {localizationService.toLanguageString('configuration.analysis.path_history.show_all_markers', 'NOT_FOUND')}
                                        </span>
                                        <Switch
                                            onChange={() => {
                                                setShowAllPoints(!showAllPoints)
                                                handleDeviceTooltipStaticMap
                                            }}
                                            checked={showAllPoints}
                                            disabled={!analyzeEnabled || noData || selectedDevices.length == 0}
                                            size={'large'}
                                        />
                                    </div>
                                </div>

                                {/*colorize with*/}
                                <div style={{ marginTop: '10px' }}>
                                    <Tooltip position="top" anchorElement="target">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span
                                                style={{ marginRight: '5px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}
                                                title={localizationService.toLanguageString('configuration.analysis.path_history.colorize_tooltip', 'NOT_FOUND')}
                                            >
                                                {localizationService.toLanguageString('configuration.analysis.path_history.colorize', 'NOT_FOUND')}
                                            </span>
                                            <Switch
                                                onLabel={showColor ? "Accuracy" : "Mac"}
                                                onChange={() => {
                                                    setShowColor(!showColor)
                                                }}
                                                checked={showColor}
                                                disabled={!analyzeEnabled || noData || selectedDevices.length == 0}
                                                size={'large'}
                                            />
                                            <b style={{ marginLeft: '10px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}>
                                                {!showColor ?
                                                    localizationService.toLanguageString('configuration.analysis.path_history.mac', 'NOT_FOUND')
                                                    :
                                                    localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')
                                                }
                                            </b>
                                        </div>
                                    </Tooltip>
                                </div>

                                {/* slider */}
                                <div style={{ marginTop: '12px', width: '250px' }}>
                                    <div
                                        style={{
                                            marginBottom: '6px',
                                            opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints ? '0.5' : '1'}`,
                                            fontWeight: 600,
                                            fontSize: '13px'
                                        }}
                                    >
                                        {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
                                    </div>

                                    <Slider
                                        className="heatmap-point-size-slider"
                                        style={{ width: '250px' }}
                                        min={timeSliderMin}
                                        max={timeSliderMax}
                                        step={1}
                                        value={step}
                                        onChange={handleSliderChange}
                                        disabled={!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints}
                                    />

                                    <div
                                        style={{
                                            marginTop: '8px',
                                            opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 || signalsGrouping || showAllPoints ? '0.5' : '1'}`,
                                            fontSize: '12px',
                                            color: '#555',
                                            width: '250px'
                                        }}
                                    >
                                        {`${sliderRangeLabels.from || ''}${sliderRangeLabels.from && spaghettiProgressiveTo ? ' - ' : ''}${spaghettiProgressiveTo || ''}`}
                                    </div>
                                </div>

                                {/*signals grouping*/}
                                <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                    <Tooltip position="top" anchorElement="target">
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                            <span
                                                style={{ width: '180px', marginRight: '10px', opacity: `${!analyzeEnabled || noData || selectedDevices.length == 0 ? '0.5' : '1'}` }}
                                                title={localizationService.toLanguageString(
                                                    'configuration.analysis.path_history.signals_grouping_tooltip',
                                                    'NOT_FOUND'
                                                )}
                                            >
                                                {localizationService.toLanguageString(
                                                    'configuration.analysis.path_history.signals_grouping',
                                                    'NOT_FOUND'
                                                )}
                                            </span>
                                            <Switch
                                                onChange={() => {
                                                    setSignalsGrouping(!signalsGrouping)
                                                    setGroupingValues({ near: 0, each: 0 })
                                                    if (signalsGrouping) {
                                                        checkResults(selectedDevices[0], undefined, !signalsGrouping)
                                                    }
                                                }}
                                                checked={signalsGrouping}
                                                disabled={noData || selectedDevices.length == 0}
                                                size={'large'}
                                            />
                                        </div>
                                    </Tooltip>
                                </div>

                                {/*average positions */}
                                {signalsGrouping && (
                                    <div style={{ display: 'grid', marginTop: '10px' }}>
                                        <Tooltip position="top" anchorElement="target">
                                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                                <span
                                                    style={{ width: '180px', marginRight: '10px' }}
                                                    title={localizationService.toLanguageString(
                                                        'configuration.analysis.path_history.average_positions_tooltip',
                                                        'NOT_FOUND'
                                                    )}
                                                >
                                                    {localizationService.toLanguageString(
                                                        'configuration.analysis.path_history.average_positions',
                                                        'NOT_FOUND'
                                                    )}
                                                </span>
                                                <Switch
                                                    onChange={() => setAveragePositions(!averagePositions)}
                                                    checked={averagePositions}
                                                    size={'large'}
                                                />
                                            </div>
                                        </Tooltip>

                                        <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                            <RadioButton
                                                style={{ marginRight: '10px' }}
                                                id="radio-near"
                                                name={'near'}
                                                value={'near'}
                                                className="primary"
                                                checked={groupingType === 'near'}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="radio-near"
                                                style={{ width: '140px', float: 'left' }}
                                            >
                                                <span>
                                                    {localizationService.toLanguageString('configuration.analysis.path_history.group_near', 'NOT_FOUND')}
                                                </span>
                                            </label>

                                            <NumericTextBox
                                                style={{ marginLeft: '10px' }}
                                                title={localizationService.toLanguageString('configuration.analysis.path_history.group_near_tooltip', 'NOT_FOUND')}
                                                defaultValue={0}
                                                width={80}
                                                min={0}
                                                max={10}
                                                size={'small'}
                                                onChange={(e) => handleValueChange('near', e.value ?? 0)}
                                                disabled={groupingType !== 'near'}
                                            />
                                        </div>

                                        <div style={{ display: 'inline-block', marginTop: '10px' }}>
                                            <RadioButton
                                                style={{ marginRight: '10px' }}
                                                id="radio-each"
                                                name='each'
                                                value='each'
                                                checked={groupingType === 'each'}
                                                onChange={handleChange}
                                            />
                                            <label htmlFor="radio-each"
                                                style={{ width: '140px', float: 'left' }}
                                            >
                                                <span>
                                                    {localizationService.toLanguageString('configuration.analysis.path_history.group_each', 'NOT_FOUND')}
                                                </span>
                                            </label>

                                            <NumericTextBox
                                                style={{ marginLeft: '10px' }}
                                                title={localizationService.toLanguageString('configuration.analysis.path_history.group_each_tooltip', 'NOT_FOUND')}
                                                defaultValue={0}
                                                width={80}
                                                min={0}
                                                max={10}
                                                size={'small'}
                                                onChange={(e) => handleValueChange('each', e.value ?? 0)}
                                                disabled={groupingType !== 'each'}
                                            />
                                        </div>

                                    </div>
                                )}

                                {selectedDevices.length != 0 && (
                                    <div style={{ width: '230px', marginTop: '20px', fontWeight: 'bold' }}>
                                        {localizationService.toLanguageString('configuration.analysis.path_history.devices_selected', 'NOT_FOUND')}:

                                        {/*show selected devices*/}
                                        <ListView
                                            data={selectedDevices}
                                            style={{ height: '60px', background: 'transparent' }}
                                            item={(props) => (
                                                <div
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'space-between',
                                                        gap: '8px',
                                                        padding: '8px',
                                                        boxShadow: 'none',
                                                        fontSize: '13px',
                                                        textTransform: 'uppercase',
                                                        borderBottom: '1px solid #ccc'
                                                    }}
                                                >
                                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                        {props.dataItem}
                                                    </span>
                                                    <div
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                        onClick={() => toggleDeviceSelection(props.dataItem)}
                                                    >
                                                        <Avatar className="trash-icon-path-history">
                                                            <SvgIcon
                                                                icon={trashIcon}
                                                                style={{ verticalAlign: 'baseline', margin: '10px' }}
                                                            />
                                                        </Avatar>
                                                    </div>
                                                </div>
                                            )}
                                        />

                                        {/*show map list*/}
                                        {!noData && analyzeEnabled &&
                                            <div>
                                                <div
                                                    style={{
                                                        fontSize: '16px',
                                                        opacity: `${!analyzeEnabled || (intervalTimeValue == null && !checked) || noData ? '0.5' : '1'}`
                                                    }}
                                                >
                                                    {localizationService.toLanguageString('configuration.analysis.path_history.maps', 'NOT_FOUND')}
                                                </div>
                                                <DropDownList
                                                    style={{
                                                        width: "180px",
                                                        opacity: `${!analyzeEnabled || (intervalTimeValue == null && !checked) || noData ? '0.5' : '1'}`
                                                    }}
                                                    data={listMapNames}
                                                    value={mapName.length > 0 ? mapName : undefined}
                                                    disabled={!analyzeEnabled || (intervalTimeValue == null && !checked) || noData}
                                                    onChange={(e: any) => changeMap(e)}
                                                />

                                                {/*toggleGeoStatic*/}
                                                {selectedMap?.georeferenced == 1 && selectedMap?.image != '' &&
                                                    <div style={{ width: '130px' }}>
                                                        <Switch
                                                            onChange={onToggleGeoStatic}
                                                            checked={toggleGeoStatic}
                                                        />
                                                        <label
                                                            style={{ margin: '0 auto' }}
                                                            className="k-ml-3"
                                                        >
                                                            {toggleGeoStatic ?
                                                                localizationService.toLanguageString('map.geo', 'NOT_FOUND')
                                                                :
                                                                localizationService.toLanguageString('map.static', 'NOT_FOUND')
                                                            }
                                                        </label>
                                                    </div>
                                                }
                                            </div>
                                        }

                                    </div>
                                )}

                            </div>

                        </div>

                    </div>

                    {/* right panel */}
                    <div
                        //className="flex-left-child-element"
                        style={{ display: 'inline', width: '100%', minHeight: '300px', padding: '10px', background: "white", overflow: 'auto' }}
                    >

                        {/*map*/}
                        {analyzeEnabled && !noData &&
                            <>
                                <div
                                    style={{
                                        display: `${analyzeEnabled && !mapGeoreferenced && !noData ? 'block' : 'none'}`,
                                        border: '1px solid black',
                                    }}
                                >
                                    {showTooltip && (
                                        <div
                                            ref={tooltipRef}
                                            onMouseMove={handleDeviceTooltipStaticMap}
                                            style={{
                                                position: "fixed",
                                                top: tooltipPos.y - 30,
                                                left: tooltipPos.x + 35,
                                                backgroundColor: "white",
                                                border: "1px solid black",
                                                padding: "8px",
                                                fontSize: "14px",
                                                pointerEvents: "none",
                                                zIndex: 9999,
                                                boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.3)",
                                                borderRadius: "4px",
                                                transform: "translate(-50%, -50%)"
                                            }}
                                            dangerouslySetInnerHTML={{ __html: tooltipContent }}
                                        />
                                    )}
                                    <TransformWrapper
                                        initialScale={0.2}
                                        initialPositionX={0}
                                        initialPositionY={0}
                                        minScale={0.2}
                                        centerOnInit={true}
                                        centerZoomedOut={true}
                                        limitToBounds={true}
                                        smooth={true}
                                        onTransformed={(_, state) => {
                                            setTransformState(state);
                                            if (lastMouseEvent) {
                                                handleDeviceTooltipStaticMap(lastMouseEvent);
                                            }
                                        }}
                                    >
                                        {({ zoomIn, zoomOut, resetTransform, centerView, zoomToElement, ...rest }) => (
                                            <>
                                                <div>
                                                    <Controls />

                                                    {
                                                        analyzeEnabled && !noData &&
                                                        <Card style={{
                                                            width: '250px',
                                                            marginLeft: '150px',
                                                            height: '32px',
                                                            top: '-5px',
                                                            borderRadius: '0px',
                                                            border: 'none',
                                                            zIndex: '1',
                                                        }}>
                                                            <CardHeader
                                                                className="expand-info-device-chart"
                                                            >
                                                                <Button
                                                                    onClick={() => setShowInfoDevice(!showInfoDevice)}
                                                                    themeColor="primary"
                                                                    style={{
                                                                        height: '32px',
                                                                        borderRadius: '0px',
                                                                        zIndex: '1',
                                                                    }}
                                                                >
                                                                    <span style={{ zIndex: '1' }}>{selectedDevices[0]}</span>
                                                                    <span
                                                                        className="k-icon k-i-arrow-chevron-down"
                                                                        style={{
                                                                            display: "inline-block",
                                                                            marginLeft: "8px",
                                                                            transition: "transform 0.2s",
                                                                            transform: showInfoDevice ? "rotate(180deg)" : "rotate(0deg)",
                                                                            zIndex: '1',
                                                                        }}
                                                                    >
                                                                    </span>
                                                                </Button>
                                                            </CardHeader>
                                                            <div
                                                                style={{
                                                                    position: "fixed",
                                                                    maxWidth: showInfoDevice ? "250px" : "0px",
                                                                    maxHeight: showInfoDevice ? "300px" : "0px",
                                                                    border: showInfoDevice ? "2px solid #198692" : "none",
                                                                    top: "135px",
                                                                    overflow: "hidden",
                                                                    transition: "max-height 0.25s ease",
                                                                    zIndex: '1',
                                                                }}
                                                            >
                                                                <CardBody style={{ position: "relative", zIndex: '1', backgroundColor: "white", paddingTop: "4px", paddingBottom: "4px" }}>
                                                                    <div style={{ display: "flex", flexDirection: "column", height: '250px' }}>

                                                                        <div>
                                                                            <strong>{localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</strong>
                                                                            &nbsp;{countWakeups}
                                                                        </div>

                                                                        <div>
                                                                            <strong>{localizationService.toLanguageString('configuration.analysis.path_history.distance', 'NOT_FOUND')}:</strong>
                                                                            &nbsp;{distance}
                                                                        </div>

                                                                        <div>
                                                                            <strong>{localizationService.toLanguageString('configuration.analysis.path_history.first', 'NOT FOUND')}:</strong>
                                                                            &nbsp;{sliderRangeLabels.from}
                                                                        </div>

                                                                        <div>
                                                                            <strong>{localizationService.toLanguageString('configuration.analysis.path_history.last', 'NOT FOUND')}:</strong>
                                                                            &nbsp;{toDateLabel || spaghettiProgressiveTo || sliderRangeLabels.to}
                                                                        </div>

                                                                        <AccuracyChart data={dataChart} localizationService={localizationService} />
                                                                    </div>
                                                                </CardBody>
                                                            </div>
                                                        </Card>
                                                    }

                                                </div>

                                                <TransformComponent
                                                    wrapperStyle={{ width: "100%", height: "100%" }}
                                                    contentStyle={{ width: mapImageWidth, height: mapImageHeight }}
                                                >
                                                    <div
                                                        ref={transformContentRef}
                                                        style={{
                                                            position: "relative",
                                                            width: mapImageWidth,
                                                            height: mapImageHeight
                                                        }}
                                                        onMouseMove={handleDeviceTooltipStaticMap}
                                                    >
                                                        <canvas ref={mapCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={zoneCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={lineCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                        <canvas ref={markerCanvasRef} style={{ position: "absolute", top: 0, left: 0 }} />
                                                    </div>
                                                </TransformComponent>

                                            </>
                                        )}
                                    </TransformWrapper>
                                </div>

                                <div
                                    ref={mapRef}
                                    style={{
                                        height: '100%',
                                        display: `${analyzeEnabled && mapGeoreferenced && !noData ? 'block' : 'none'}`
                                    }}
                                >
                                    {listMaps.length != 0 && mapGeoreferenced &&
                                        <AzureMapSpaghettiChart
                                            key={checkedOnlyBound ? 'azure-map-only-bound' : 'azure-map-all-devices'}
                                            map={selectedMap}
                                            zones={showZoneMapGeo ?? []}
                                            coordinatesList={coordinates}
                                            isAdvancedDate={checked}
                                            formattedIntervalDate={formattedIntervalDate}
                                            fromDate={dateTimeState.fromHour.toLocaleString()}
                                            toDate={dateTimeState.toHour.toLocaleString()}
                                            countWakeups={countWakeups}
                                            showColor={showColor}
                                            showSlider={false}
                                            externalSliderPercent={step}
                                            onSliderDateChange={handleGeoSliderDateChange}
                                            showAllPoints={showAllPoints}
                                            selectedDevices={selectedDevices}
                                            signalsGrouping={signalsGrouping}
                                        />
                                    }
                                </div>
                                {/*}*/}
                            </>
                        }

                    </div>
                </div >
            }
        </>
    )
}